/**
 * ZEN ERP — OrderForm Yardımcı Bileşenleri
 * OrderForm.jsx'ten çıkarıldı: MetalFieldRow, özel form bileşenleri,
 * FabricCombobox, RalCombobox, ExtrasChecklist, SelectRow vb.
 */
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { AlertTriangle, Search, ShoppingCart, X } from "lucide-react";
import { toast } from "sonner";
import { UICtx, StockSettingsCtx } from "../appContexts";
import api from "../api";
import { convertPrice } from "../appUtils";
import { triggerQuickAdd, focusMeasureField } from "../utils/fastEntry";

// Ctrl+K = föyün birincil aksiyonu (Sepete Ekle) + odak EN'e (bkz. OrderForm.jsx notu)
const quickAddKeyHandler = (e) => {
  const root = e.currentTarget;
  if (triggerQuickAdd(e, root)) setTimeout(() => focusMeasureField(root), 0);
};
import { GROUP_EXTRAS, slatSwatchStyle } from "../utils/pdfUtils";
import {
  METAL_JALUZI_SLUG, DIKEY_RAY_PRICES,
  STOR_KUMAS_GROUPS, AKRILIK_KUMAS_GROUP, FABRIC_GROUP_LABELS,
} from "../utils/quoteUtils";
import { roundRecipeWidth, RECIPE_CODES, findPriceItem } from "../utils/storRecipe";

export function MetalFieldRow({ label, required = false, children, testid }) {
  const { tr, lang } = useContext(UICtx);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[190px_minmax(0,1fr)] gap-2 sm:gap-5 px-3 sm:px-4 py-3.5 border-b border-slate-200 dark:border-[#22304A] items-center min-w-0 w-full" data-testid={testid}>
      <label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-300">
        {required && <span className="text-[#E5322A] mr-1">*</span>}{label}
      </label>
      <div className="min-w-0 max-w-full">{children}</div>
    </div>
  );
}

// ── Slat Rengi Dropdown (Metal Jaluzi) ────────────────────────────────────────
function SlatColorDropdown({ slats, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = slats.find(a => (a.code || a.id) === value);

  // Dışarı tıklanınca kapat
  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // Açılınca arama kutusuna odaklan
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else setSearch("");
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? slats.filter(a => (a.code || "").toLowerCase().includes(q) || (a.label || "").toLowerCase().includes(q))
    : slats;

  const swatchBg = (slat) => {
    if (slat.imageBase64) return `url("${slat.imageBase64}")`;
    return slat.hexColor || "#CCCCCC";
  };

  return (
    <div ref={ref} className="relative w-full">
      {/* Seçim butonu */}
      <button
        type="button"
        data-testid="slat-color-dropdown-trigger"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-all text-left
          ${open
            ? "border-[#E5322A]/60 ring-1 ring-[#E5322A]/20 bg-white dark:bg-[#131B2E]"
            : "border-slate-200 dark:border-[#31405D] bg-slate-50 dark:bg-[#10182A] hover:border-slate-400 dark:hover:border-[#4a5a7a]"
          }`}
      >
        {selected ? (
          <>
            <span className="w-6 h-6 rounded shrink-0 border border-slate-300 dark:border-[#31405D]"
              style={selected.imageBase64
                ? { backgroundImage: `url("${selected.imageBase64}")`, backgroundSize: "cover", backgroundPosition: "center" }
                : { background: selected.hexColor || "#CCCCCC" }} />
            <span className="flex-1 min-w-0">
              <span className="text-xs font-bold text-slate-800 dark:text-slate-100">{selected.code}</span>
              <span className="text-[11px] text-slate-500 ml-1.5">{selected.label}</span>
            </span>
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange("", ""); }}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0" title="Temizle">
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <span className="w-6 h-6 rounded shrink-0 border border-dashed border-slate-300 dark:border-[#31405D] bg-slate-100 dark:bg-[#1e2d45]" />
            <span className="text-xs text-slate-400 flex-1">Renk seçiniz…</span>
            <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-white dark:bg-[#131B2E] rounded-xl border border-slate-200 dark:border-[#31405D] shadow-2xl overflow-hidden">
          {/* Arama */}
          <div className="p-2 border-b border-slate-100 dark:border-[#22304A]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                data-testid="slat-color-search"
                placeholder="Kod veya renk ara…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-8 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-[#31405D] bg-slate-50 dark:bg-[#10182A] outline-none focus:border-[#E5322A]/60 transition-all"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="slat-color-search-clear">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            {q && <div className="text-[10px] text-slate-400 mt-1 ml-1">{filtered.length} sonuç</div>}
          </div>

          {/* Liste */}
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400" data-testid="slat-color-no-results">
                "{search}" için renk bulunamadı
              </div>
            ) : filtered.map(slat => {
              const isSel = (slat.code || slat.id) === value;
              return (
                <button
                  key={slat.id}
                  type="button"
                  data-testid={`slat-color-opt-${slat.code}`}
                  onClick={() => { onChange(slat.code || slat.id, slat.label || ""); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-[#1e2d45]
                    ${isSel ? "bg-[#E5322A]/8 dark:bg-[#E5322A]/12" : ""}`}
                >
                  <span
                    className="w-7 h-7 rounded shrink-0 border border-slate-200 dark:border-[#31405D]"
                    style={slat.imageBase64
                      ? { backgroundImage: `url("${slat.imageBase64}")`, backgroundSize: "cover", backgroundPosition: "center" }
                      : { background: slat.hexColor || "#CCCCCC" }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">{slat.code}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{slat.label}</span>
                  </span>
                  {isSel && <span className="text-[#E5322A] shrink-0">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function SadeceKumasOrderForm({ group, item, setItem, assets, editingItemIndex, onCancel, onAdd }) {
  const { tr, lang } = useContext(UICtx);
  const update = (key, value) => setItem({ ...item, [key]: value });
  const storFabrics = assets.filter(a => a.kind === "fabric" && (a.group === "stor" || STOR_KUMAS_GROUPS.includes(a.group) || a.group === AKRILIK_KUMAS_GROUP));
  return (
    <div className="p-3 sm:p-6 w-full max-w-full min-w-0 overflow-x-hidden" onKeyDown={quickAddKeyHandler} data-testid="sadece-kumas-form">
      <button onClick={onCancel} className="zen-btn-ghost !text-xs !py-1 mb-3" data-testid="sadece-kumas-close-top">{tr("← Ürün Listesine Geri Dön")}</button>
      {editingItemIndex !== null && (
        <div className="zen-panel !p-2.5 mb-3 bg-[#E5322A]/10 border-[#E5322A]/30 text-xs font-semibold text-[#E5322A]" data-testid="sadece-kumas-editing-alert">
          Kumaş kalemi düzenleniyor · #{editingItemIndex + 1}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] w-full max-w-full min-w-0">
        <MetalFieldRow label={tr("Adet")} required testid="sadece-kumas-quantity-row">
          <div className="inline-grid grid-cols-[44px_80px_44px] rounded-md overflow-hidden border border-slate-200 dark:border-[#31405D]">
            <button type="button" data-testid="sadece-kumas-qty-decrease" onClick={() => update("qty", Math.max(1, (item.qty || 1) - 1))} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">−</button>
            <input type="number" min="1" data-testid="sadece-kumas-qty-input" className="h-11 text-center bg-white dark:bg-[#10182A] outline-none" value={item.qty || 1} onChange={(e) => update("qty", Math.max(1, Number(e.target.value) || 1))} />
            <button type="button" data-testid="sadece-kumas-qty-increase" onClick={() => update("qty", (item.qty || 1) + 1)} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">+</button>
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Kumaş")} required testid="sadece-kumas-fabric-row">
          <FabricCombobox
            testid="sadece-kumas-fabric"
            formSlug="stor"
            options={expandFabricOptions(storFabrics)}
            value={item.fabricCode || ""}
            display={item.fabric || ""}
            onSelect={(o) => {
              if (!o.value) { setItem({ ...item, fabricCode: "", fabric: "", fabricPrice: 0, fabricCurrency: "", fabricGroup: "", ...fabricStockFields(null) }); return; }
              const f = o.fabric;
              setItem({ ...item, fabricCode: o.value, fabric: o.label, fabricGroup: f?.group || "", fabricPrice: f?.price || 0, fabricCurrency: f?.price > 0 ? (f?.currency || "EUR") : "", ...fabricStockFields(f) });
            }}
          />
        </MetalFieldRow>
        {(item.fabricOutOfStock || item.fabricPurchase > 0) && (
          <div className="px-3 py-2 border-t border-slate-200 dark:border-[#22304A]">
            <FabricStockPanel item={item} setItem={setItem} margin={null} testid="sadece-kumas" />
          </div>
        )}

        {[["En", "widthCm", "sadece-kumas-width"], ["Boy", "heightCm", "sadece-kumas-height"]].map(([label, field, testid]) => (
          <MetalFieldRow key={field} label={label} required testid={`${testid}-row`}>
            <div className="flex flex-1">
              <input type="number" min="1" data-testid={testid} className="zen-input !py-1.5 !rounded-r-none" value={item[field] || ""} onChange={(e) => update(field, Number(e.target.value))} />
              <span className="w-16 shrink-0 border border-l-0 border-slate-200 dark:border-[#31405D] rounded-r-md flex items-center justify-center text-xs font-bold text-slate-500 bg-slate-100 dark:bg-[#22304A]">CM</span>
            </div>
          </MetalFieldRow>
        ))}

        <MetalFieldRow label={tr("Not")} testid="sadece-kumas-note-row">
          <input data-testid="sadece-kumas-note" className="zen-input !py-1.5" value={item.note || ""} onChange={(e) => update("note", e.target.value)} />
        </MetalFieldRow>
      </div>

      <div className="text-[11px] text-slate-500 mt-2" data-testid="sadece-kumas-info">Stor kataloğu kumaşları · satış fiyatı stor perde fiyatının %35 altındadır · Akrilik kumaş $15/m² liste fiyatından satılır (indirim uygulanmaz) · KDV %10</div>

      <div className="flex flex-wrap justify-center gap-3 pt-4">
        <button onClick={onCancel} className="zen-btn-ghost w-full sm:w-auto sm:min-w-32" data-testid="sadece-kumas-close">{tr("Kapat")}</button>
        <button onClick={() => { if (item.fabricOutOfStock && !item.stockAck) { toast.error("Bu kumaş STOK DIŞI — devam etmek için onay kutusunu işaretleyin"); return; } onAdd(); }} className="zen-btn-primary w-full sm:w-auto sm:min-w-40" data-testid="sadece-kumas-add"><ShoppingCart className="w-4 h-4" />{tr("Sepete Ekle")}</button>
      </div>
    </div>
  );
}

export function StorSistemOrderForm({ group, item, setItem, editingItemIndex, onCancel, onAdd, assets = [], priceItems = [] }) {
  const { tr, lang } = useContext(UICtx);
  const update = (key, value) => setItem({ ...item, [key]: value });
  // REÇETE (31.10): yuvarlama reçete kuralıyla; fiyatlar LİSTEDEN (Fiyat Kalemleri + Varlıklar)
  const sistem = item.sistem === "motorlu" ? "motorlu" : "manuel";
  const mt = item.widthCm ? roundRecipeWidth(Number(item.widthCm)) / 100 : 0;
  const motorOptions = (assets || []).filter((a) => a && a.kind === "motor" &&
    Array.isArray(a.groupSlugs) && a.groupSlugs.includes("stor_sistem"));
  const kasaPriceLabel = (kasa) => {
    const p = findPriceItem(priceItems, (RECIPE_CODES.mekanizma[sistem] || {})[kasa]);
    return p ? `${p.basePrice.toLocaleString("tr-TR")} TL/mt` : "fiyat yok";
  };
  return (
    <div className="p-3 sm:p-6 w-full max-w-full min-w-0 overflow-x-hidden" onKeyDown={quickAddKeyHandler} data-testid="stor-sistem-form">
      <button onClick={onCancel} className="zen-btn-ghost !text-xs !py-1 mb-3" data-testid="stor-sistem-close-top">{tr("← Ürün Listesine Geri Dön")}</button>
      {editingItemIndex !== null && (
        <div className="zen-panel !p-2.5 mb-3 bg-[#E5322A]/10 border-[#E5322A]/30 text-xs font-semibold text-[#E5322A]" data-testid="stor-sistem-editing-alert">
          Stor Sistem kalemi düzenleniyor · #{editingItemIndex + 1}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] w-full max-w-full min-w-0">
        <MetalFieldRow label={tr("Adet")} required testid="stor-sistem-quantity-row">
          <div className="inline-grid grid-cols-[44px_80px_44px] rounded-md overflow-hidden border border-slate-200 dark:border-[#31405D]">
            <button type="button" data-testid="stor-sistem-qty-decrease" onClick={() => update("qty", Math.max(1, (item.qty || 1) - 1))} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">−</button>
            <input type="number" min="1" data-testid="stor-sistem-qty-input" className="h-11 text-center bg-white dark:bg-[#10182A] outline-none" value={item.qty || 1} onChange={(e) => update("qty", Math.max(1, Number(e.target.value) || 1))} />
            <button type="button" data-testid="stor-sistem-qty-increase" onClick={() => update("qty", (item.qty || 1) + 1)} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">+</button>
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Sistem Tipi")} testid="stor-sistem-type-row">
          <select data-testid="stor-sistem-type" className="zen-input !py-1.5" value={sistem} onChange={(e) => {
            const v = e.target.value;
            setItem({ ...item, sistem: v, ...(v === "motorlu" ? {} : { motorLabel: "", motorName: "", motor: false, motorPrice: 0, motorCurrency: "" }) });
          }}>
            <option value="manuel">Manuel Sistem (32Ø)</option>
            <option value="motorlu">Motorlu Sistem (47Ø)</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Kasa Tipi")} required testid="stor-sistem-kasa-row">
          <select data-testid="stor-sistem-kasa" className="zen-input !py-1.5" value={item.kasaTipi || ""} onChange={(e) => {
            const t = e.target.value;
            setItem({ ...item, kasaTipi: t, product: t ? `Kumaşsız Stor Sistemi · ${t}` : group.name });
          }}>
            <option value="">{tr("Kasa Tipi Seçiniz")}</option>
            {["Kapalı Kasa", "Açık Kasa"].map((t) => <option key={t} value={t}>{t} · {kasaPriceLabel(t)}</option>)}
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Genişlik")} required testid="stor-sistem-width-row">
          <div className="flex items-center gap-2 w-full">
            <div className="flex flex-1">
              <input type="number" min="1" data-testid="stor-sistem-width" className="zen-input !py-1.5 !rounded-r-none" value={item.widthCm || ""} onChange={(e) => update("widthCm", Number(e.target.value))} />
              <span className="w-16 shrink-0 border border-l-0 border-slate-200 dark:border-[#31405D] rounded-r-md flex items-center justify-center text-xs font-bold text-slate-500 bg-slate-100 dark:bg-[#22304A]" data-testid="stor-sistem-width-unit">CM</span>
            </div>
            {mt > 0 && <span className="text-xs font-bold text-slate-500 whitespace-nowrap" data-testid="stor-sistem-mt-info">→ {mt.toLocaleString("tr-TR")} mt</span>}
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={<span>{tr("Yükseklik")}<span className="text-[9px] font-normal text-slate-400 ml-1">(bilgi)</span></span>} testid="stor-sistem-height-row">
          <div className="flex items-center gap-2 w-full">
            <div className="flex flex-1">
              <input type="number" min="1" data-testid="stor-sistem-height" className="zen-input !py-1.5 !rounded-r-none" value={item.heightCm || ""} onChange={(e) => update("heightCm", Number(e.target.value))} />
              <span className="w-16 shrink-0 border border-l-0 border-slate-200 dark:border-[#31405D] rounded-r-md flex items-center justify-center text-xs font-bold text-slate-500 bg-slate-100 dark:bg-[#22304A]" data-testid="stor-sistem-height-unit">CM</span>
            </div>
            <span className="text-[10px] text-slate-500 whitespace-nowrap">imalat içindir — fiyatı etkilemez</span>
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={item.motor ? tr("Motor Yönü") : tr("Zincir Yönü")} testid="stor-sistem-side-row">
          <select data-testid="stor-sistem-side" className="zen-input !py-1.5" value={item.controlSide || ""} onChange={(e) => update("controlSide", e.target.value)}>
            <option value="">{tr("Seçiniz")}</option>
            <option value="Sağ">{tr("Sağ")}</option>
            <option value="Sol">{tr("Sol")}</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Zincir")} testid="stor-sistem-chain-row">
          <select data-testid="stor-sistem-chain" className="zen-input !py-1.5" value={item.zincirTipi || ""} onChange={(e) => update("zincirTipi", e.target.value)}>
            <option value="">{tr("Seçiniz")}</option>
            <option value="Metal Zincir">{tr("Metal Zincir")}</option>
            <option value="Plastik Zincir">{tr("Plastik Zincir")}</option>
          </select>
        </MetalFieldRow>

        {sistem === "motorlu" && (
        <MetalFieldRow label={<span>{tr("Motor")}<span className="text-[#E5322A] ml-1">*</span></span>} testid="stor-sistem-motor-row">
          <div>
            <select data-testid="stor-sistem-motor" className="zen-input !py-1.5" value={item.motorLabel || ""} onChange={(e) => {
              const label = e.target.value;
              const asset = motorOptions.find((a) => (a.label || a.name) === label);
              setItem({ ...item, motorName: label, motorLabel: label, motor: !!label,
                motorPrice: asset ? Number(asset.price) || 0 : 0, motorCurrency: asset ? asset.currency : "USD" });
            }}>
              <option value="">{tr("Motor Seçiniz (zorunlu)")}</option>
              {motorOptions.map((a) => <option key={a.id || a.label} value={a.label || a.name}>{a.label || a.name} · {a.price} {a.currency}/ad</option>)}
            </select>
            {Number(item.widthCm) > 0 && Number(item.widthCm) < 60 && (
              <div className="text-[10px] font-semibold text-[#E5322A] mt-1">Motorlu sistemde en az 60 cm genişlik gerekli</div>
            )}
            {!item.motorLabel && (
              <div className="text-[10px] text-slate-500 mt-1">Motorlu sistemde motor seçimi zorunludur</div>
            )}
          </div>
        </MetalFieldRow>
        )}

        <MetalFieldRow label={tr("Not")} testid="stor-sistem-note-row">
          <input data-testid="stor-sistem-note" className="zen-input !py-1.5" value={item.note || ""} onChange={(e) => update("note", e.target.value)} />
        </MetalFieldRow>
      </div>

      <div className="flex flex-wrap justify-center gap-3 pt-5">
        <button onClick={onCancel} className="zen-btn-ghost w-full sm:w-auto sm:min-w-32" data-testid="stor-sistem-close">{tr("Kapat")}</button>
        <button onClick={onAdd} className="zen-btn-primary w-full sm:w-auto sm:min-w-40" data-testid="stor-sistem-add"><ShoppingCart className="w-4 h-4" />{tr("Sepete Ekle")}</button>
      </div>
    </div>
  );
}

export function DikeyRayOrderForm({ group, item, setItem, editingItemIndex, onCancel, onAdd }) {
  const { tr, lang } = useContext(UICtx);
  const update = (key, value) => setItem({ ...item, [key]: value });
  const mt = item.widthCm ? Math.ceil(Number(item.widthCm) / 10) / 10 : 0;
  return (
    <div className="p-3 sm:p-6 w-full max-w-full min-w-0 overflow-x-hidden" onKeyDown={quickAddKeyHandler} data-testid="dikey-ray-form">
      <button onClick={onCancel} className="zen-btn-ghost !text-xs !py-1 mb-3" data-testid="dikey-ray-close-top">{tr("← Ürün Listesine Geri Dön")}</button>
      {editingItemIndex !== null && (
        <div className="zen-panel !p-2.5 mb-3 bg-[#E5322A]/10 border-[#E5322A]/30 text-xs font-semibold text-[#E5322A]" data-testid="dikey-ray-editing-alert">
          Dikey Ray kalemi düzenleniyor · #{editingItemIndex + 1}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] w-full max-w-full min-w-0">
        <MetalFieldRow label={tr("Adet")} required testid="dikey-ray-quantity-row">
          <div className="inline-grid grid-cols-[44px_80px_44px] rounded-md overflow-hidden border border-slate-200 dark:border-[#31405D]">
            <button type="button" data-testid="dikey-ray-qty-decrease" onClick={() => update("qty", Math.max(1, (item.qty || 1) - 1))} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">−</button>
            <input type="number" min="1" data-testid="dikey-ray-qty-input" className="h-11 text-center bg-white dark:bg-[#10182A] outline-none" value={item.qty || 1} onChange={(e) => update("qty", Math.max(1, Number(e.target.value) || 1))} />
            <button type="button" data-testid="dikey-ray-qty-increase" onClick={() => update("qty", (item.qty || 1) + 1)} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">+</button>
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Ray Tipi")} required testid="dikey-ray-type-row">
          <select data-testid="dikey-ray-type" className="zen-input !py-1.5" value={item.rayTipi || ""} onChange={(e) => {
            const t = e.target.value;
            setItem({ ...item, rayTipi: t, product: t ? `Dikey Perde Rayı ${t}` : group.name });
          }}>
            <option value="">{tr("Ray Tipi Seçiniz")}</option>
            {Object.entries(DIKEY_RAY_PRICES).map(([t, p]) => <option key={t} value={t}>{t} Ray · {p.toLocaleString("tr-TR")} €/mt</option>)}
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Slat Genişliği")} testid="dikey-ray-slat-width-row">
          <select data-testid="dikey-ray-slat-width" className="zen-input !py-1.5" value={item.slatGenisligi || ""} onChange={(e) => update("slatGenisligi", e.target.value)}>
            <option value="">{tr("Seçiniz")}</option>
            <option value="8,9 cm">8,9 cm</option>
            <option value="12,7 cm">12,7 cm</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Ray Genişliği")} required testid="dikey-ray-width-row">
          <div className="flex items-center gap-2 w-full">
            <div className="flex flex-1">
              <input type="number" min="1" data-testid="dikey-ray-width" className="zen-input !py-1.5 !rounded-r-none" value={item.widthCm || ""} onChange={(e) => update("widthCm", Number(e.target.value))} />
              <span className="w-16 shrink-0 border border-l-0 border-slate-200 dark:border-[#31405D] rounded-r-md flex items-center justify-center text-xs font-bold text-slate-500 bg-slate-100 dark:bg-[#22304A]" data-testid="dikey-ray-width-unit">CM</span>
            </div>
            {mt > 0 && <span className="text-xs font-bold text-slate-500 whitespace-nowrap" data-testid="dikey-ray-mt-info">→ {mt.toLocaleString("tr-TR")} mt</span>}
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Motor")} testid="dikey-ray-motor-row">
          <div className="flex items-center gap-2">
            <select data-testid="dikey-ray-motor" className="zen-input !py-1.5 flex-1" value={item.motorLabel || ""} onChange={(e) => setItem({ ...item, motorLabel: e.target.value, motor: !!e.target.value, motorPrice: e.target.value ? (item.motorPrice || 300) : 0, motorCurrency: "EUR" })}>
              <option value="">{tr("Seçiniz (opsiyonel)")}</option>
              <option value="Novo Dikey Perde Motoru">Novo Dikey Perde Motoru</option>
            </select>
            {item.motorLabel && (
              <div className="flex items-center border border-slate-200 dark:border-[#31405D] rounded-lg overflow-hidden shrink-0">
                <input
                  type="number" min="0" step="1"
                  data-testid="dikey-ray-motor-price"
                  className="w-20 h-9 text-right text-xs font-bold bg-white dark:bg-[#10182A] outline-none px-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={item.motorPrice ?? 300}
                  onChange={(e) => update("motorPrice", Number(e.target.value) || 0)}
                />
                <span className="px-2 text-[10px] font-bold text-slate-400 border-l border-slate-200 dark:border-[#31405D] h-9 flex items-center bg-slate-50 dark:bg-[#1e2d45]">EUR</span>
              </div>
            )}
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Not")} testid="dikey-ray-note-row">
          <input data-testid="dikey-ray-note" className="zen-input !py-1.5" value={item.note || ""} onChange={(e) => update("note", e.target.value)} />
        </MetalFieldRow>
      </div>

      <div className="flex flex-wrap justify-center gap-3 pt-5">
        <button onClick={onCancel} className="zen-btn-ghost w-full sm:w-auto sm:min-w-32" data-testid="dikey-ray-close">{tr("Kapat")}</button>
        <button onClick={onAdd} className="zen-btn-primary w-full sm:w-auto sm:min-w-40" data-testid="dikey-ray-add"><ShoppingCart className="w-4 h-4" />{tr("Sepete Ekle")}</button>
      </div>
    </div>
  );
}

export function MetalJaluziOrderForm({ group, item, setItem, assets, editingItemIndex, onCancel, onAdd }) {
  const { tr, lang } = useContext(UICtx);
  const update = (key, value) => setItem({ ...item, [key]: value });
  const metalSlats = assets.filter(a => a.kind === "metal_slat");

  return (
    <div className="p-3 sm:p-6 w-full max-w-full min-w-0 overflow-x-hidden" onKeyDown={quickAddKeyHandler} data-testid="metal-jaluzi-form">
      <button onClick={onCancel} className="zen-btn-ghost !text-xs !py-1 mb-3" data-testid="metal-jaluzi-close-top">{tr("← Ürün Listesine Geri Dön")}</button>
      {editingItemIndex !== null && (
        <div className="zen-panel !p-2.5 mb-3 bg-[#E5322A]/10 border-[#E5322A]/30 text-xs font-semibold text-[#E5322A]" data-testid="metal-jaluzi-editing-alert">
          Metal Jaluzi kalemi düzenleniyor · #{editingItemIndex + 1}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] w-full max-w-full min-w-0">
        <MetalFieldRow label={tr("Adet")} required testid="metal-jaluzi-quantity-row">
          <div className="inline-grid grid-cols-[44px_80px_44px] rounded-md overflow-hidden border border-slate-200 dark:border-[#31405D]">
            <button type="button" data-testid="metal-jaluzi-qty-decrease" onClick={() => update("qty", Math.max(1, (item.qty || 1) - 1))} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">−</button>
            <input type="number" min="1" data-testid="metal-jaluzi-qty-input" className="h-11 text-center bg-white dark:bg-[#10182A] outline-none" value={item.qty || 1} onChange={(e) => update("qty", Math.max(1, Number(e.target.value) || 1))} />
            <button type="button" data-testid="metal-jaluzi-qty-increase" onClick={() => update("qty", (item.qty || 1) + 1)} className="h-11 bg-slate-100 dark:bg-[#22304A] hover:bg-slate-200 dark:hover:bg-[#2B3A58] transition-colors text-lg">+</button>
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Ürün / Slat")} required testid="metal-jaluzi-slat-size-row">
          <select data-testid="metal-jaluzi-slat-size" className="zen-input !py-1.5" value={item.slatVariantKey || ""} onChange={(e) => {
            const variant = (group.slatVariants || []).find(v => v.key === e.target.value);
            setItem({ ...item, slatVariantKey: e.target.value, slatSize: variant?.size || "", slatColorCode: "", slatColor: "", product: variant ? `Metal Jaluzi ${variant.label}` : group.name });
          }}>
            <option value="">{tr("Ürün Seçiniz")}</option>
            {(group.slatVariants || []).map(v => <option key={v.key} value={v.key}>Metal Jaluzi {v.label} · {v.priceEUR} €/m²</option>)}
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Slat Rengi")} testid="metal-jaluzi-color-row">
          {(() => {
            const sizeToGroup = { "16": "metal_16", "25": "metal_25", "50": "metal_50" };
            const targetGroup = sizeToGroup[item.slatSize] || null;
            const sizeSlats = targetGroup ? metalSlats.filter(a => a.group === targetGroup) : metalSlats;
            if (!item.slatSize) return <div className="text-xs text-slate-400 py-1.5">Önce ürün/slat seçiniz</div>;
            if (sizeSlats.length === 0) return <div className="text-xs text-slate-400 py-1.5">Bu kalınlık için renk kartı bulunamadı</div>;
            return (
              <SlatColorDropdown
                slats={sizeSlats}
                value={item.slatColorCode || ""}
                onChange={(code, label) => setItem({ ...item, slatColorCode: code, slatColor: label })}
              />
            );
          })()}
        </MetalFieldRow>

        {[{ label: "En", field: "widthCm", testid: "metal-jaluzi-width" }, { label: "Boy", field: "heightCm", testid: "metal-jaluzi-height" }].map(dimension => (
          <MetalFieldRow key={dimension.field} label={dimension.label} required testid={`${dimension.testid}-row`}>
            <div className="flex">
              <input type="number" min="1" data-testid={dimension.testid} className="zen-input !py-1.5 !rounded-r-none" value={item[dimension.field] || ""} onChange={(e) => update(dimension.field, Number(e.target.value))} />
              <span className="w-16 shrink-0 border border-l-0 border-slate-200 dark:border-[#31405D] rounded-r-md flex items-center justify-center text-xs font-bold text-slate-500 bg-slate-100 dark:bg-[#22304A]" data-testid={`${dimension.testid}-unit`}>CM</span>
            </div>
          </MetalFieldRow>
        ))}

        <MetalFieldRow label={tr("Zıgıl Sistem")} testid="metal-jaluzi-zigil-row">
          <select data-testid="metal-jaluzi-zigil" className="zen-input !py-1.5" value={item.zigilSistem || ""} onChange={(e) => update("zigilSistem", e.target.value)}>
            <option value="">{tr("Seçiniz (opsiyonel)")}</option>
            <option value="Zıgıl Aparatı">{tr("Zıgıl Aparatı")}</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Motor")} testid="metal-jaluzi-motor-row">
          <select data-testid="metal-jaluzi-motor" className="zen-input !py-1.5" value={item.motorLabel || ""} onChange={(e) => setItem({ ...item, motorLabel: e.target.value, motor: !!e.target.value })}>
            <option value="">{tr("Seçiniz (opsiyonel)")}</option>
            <option value="25mm AOK Motor">25mm AOK Motor</option>
            <option value="25mm Somfy Motor">25mm Somfy Motor</option>
            <option value="25mm Novo Motor">25mm Novo Motor</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Motor Kumandası")} testid="metal-jaluzi-control-row">
          <select data-testid="metal-jaluzi-control" className="zen-input !py-1.5" value={item.controlLabel || ""} onChange={(e) => setItem({ ...item, controlLabel: e.target.value, control: e.target.value })}>
            <option value="">{tr("Seçiniz (opsiyonel)")}</option>
            <option value="Tek Kanal">{tr("Tek Kanal")}</option>
            <option value="5 Kanal">{tr("5 Kanal")}</option>
            <option value="16 Kanal">{tr("16 Kanal")}</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Çift Cam Arası")} testid="metal-jaluzi-double-glass-row">
          <select data-testid="metal-jaluzi-double-glass" className="zen-input !py-1.5" value={item.ciftCamArasi || ""} onChange={(e) => update("ciftCamArasi", e.target.value)}>
            <option value="">{tr("Seçiniz (opsiyonel)")}</option>
            <option value="Var (Butonlu Sistem)">{tr("Var (Butonlu Sistem)")}</option>
          </select>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Ekstralar")} testid="metal-jaluzi-extras-row">
          <div className="py-1.5 w-full">
            <ExtrasChecklist slug={METAL_JALUZI_SLUG} item={item} setItem={setItem} testidPrefix="metal-jaluzi" />
          </div>
        </MetalFieldRow>

        <MetalFieldRow label={tr("Not")} testid="metal-jaluzi-note-row">
          <input data-testid="metal-jaluzi-note" className="zen-input !py-1.5" value={item.note || ""} onChange={(e) => update("note", e.target.value)} />
        </MetalFieldRow>
      </div>

      <div className="flex flex-wrap justify-center gap-3 pt-5">
        <button onClick={onCancel} className="zen-btn-ghost w-full sm:w-auto sm:min-w-32" data-testid="metal-jaluzi-close">{tr("Kapat")}</button>
        <button onClick={onAdd} className="zen-btn-primary w-full sm:w-auto sm:min-w-40" data-testid="metal-jaluzi-add"><ShoppingCart className="w-4 h-4" />{tr("Sepete Ekle")}</button>
      </div>
    </div>
  );
}

// Aranabilir kumaş seçici: kod aralıklarını (codeEnd) tek tek renk varyantlarına açar
export const expandFabricOptions = (fabs) => {
  const out = [];
  for (const f of fabs) {
    const stockSuffix = f.stockNote === "stok sorunuz" ? " (stok sorunuz)" : "";
    const s = parseInt(f.code, 10);
    const e = parseInt(f.codeEnd || "", 10);
    if (!isNaN(s) && !isNaN(e) && e > s && e - s < 500) {
      for (let c = s; c <= e; c++) out.push({ value: String(c), label: `${f.label} ${c}${stockSuffix}`, fabric: f });
    } else {
      const baseLabel = !f.code || f.label === f.code ? f.label : `${f.label} ${f.code}`;
      out.push({ value: f.code, label: `${baseLabel}${stockSuffix}`, fabric: f });
    }
  }
  return out;
};

export function ExtrasChecklist({ slug, item, setItem, testidPrefix = "pf", filterFn, cols = 2 }) {
  const { tr, lang } = useContext(UICtx);
  const defs = GROUP_EXTRAS[slug];
  if (!defs) return null;
  const visibleDefs = filterFn ? defs.filter(filterFn) : defs;
  if (visibleDefs.length === 0) return null;
  const tag = (x) => x.displayStr ? x.displayStr : x.type === "pct" ? `+%${x.pct}` : x.type === "pct_renk" ? `+%${x.pct}/renk` : `${x.price}€${x.type === "m2" ? "/m²" : "/ad"}`;
  const hasRenk = (item.pliseExtras || []).some(k => defs.find(d => d.key === k)?.type === "pct_renk");
  return (
    <div className={`flex-1 grid grid-cols-${cols} gap-1.5`} data-testid={`${testidPrefix}-plise-extras`}>
      {visibleDefs.map(x => (
        <label key={x.key} className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid={`${testidPrefix}-extra-${x.key}`}>
          <input type="checkbox" className="accent-[#E5322A]" checked={(item.pliseExtras || []).includes(x.key)} onChange={(e) => {
            const cur = item.pliseExtras || [];
            const next = e.target.checked ? [...cur, x.key] : cur.filter(k => k !== x.key);
            setItem({ ...item, pliseExtras: next, extrasLabel: next.map(k => defs.find(d => d.key === k)?.label).filter(Boolean).join(", ") });
          }} />
          <span>{x.label} <span className="text-slate-400">({tag(x)})</span></span>
        </label>
      ))}
      {hasRenk && (
        <label className="flex items-center gap-2 text-xs">
          <span className="text-slate-500 font-semibold">{tr("Renk sayısı:")}</span>
          <input type="number" min="1" className="zen-input !py-1 !w-16 text-center" value={item.renkSayisi || 2} onChange={(e) => setItem({ ...item, renkSayisi: Number(e.target.value) })} data-testid={`${testidPrefix}-renk-sayisi`} />
        </label>
      )}
    </div>
  );
}

// ─── Standard RAL Classic Renk Paleti ──────────────────────────────────────
const RAL_COLORS = [
  // Sarı
  {code:"RAL 1000",name:"Yeşilimsi Bej",hex:"#BEBD7F"},{code:"RAL 1001",name:"Bej",hex:"#C2B078"},{code:"RAL 1002",name:"Kum Sarısı",hex:"#C6A664"},{code:"RAL 1003",name:"Sinyalizasyon Sarısı",hex:"#E5BE01"},{code:"RAL 1004",name:"Altın Sarısı",hex:"#CDA434"},{code:"RAL 1005",name:"Bal Sarısı",hex:"#A98307"},{code:"RAL 1006",name:"Mısır Sarısı",hex:"#E4A010"},{code:"RAL 1007",name:"Nergis Sarısı",hex:"#DC9D00"},{code:"RAL 1011",name:"Kahverengi Bej",hex:"#8A6642"},{code:"RAL 1012",name:"Limon Sarısı",hex:"#C7B446"},{code:"RAL 1013",name:"İnci Beyazı",hex:"#EAE6CA"},{code:"RAL 1014",name:"Fildişi",hex:"#E1CC4F"},{code:"RAL 1015",name:"Açık Fildişi",hex:"#E6D690"},{code:"RAL 1016",name:"Kükürt Sarısı",hex:"#EDFF21"},{code:"RAL 1017",name:"Safran Sarısı",hex:"#F5D033"},{code:"RAL 1018",name:"Çinko Sarısı",hex:"#F8F32B"},{code:"RAL 1019",name:"Gri Bej",hex:"#9E9764"},{code:"RAL 1020",name:"Zeytin Sarısı",hex:"#999950"},{code:"RAL 1021",name:"Kolza Sarısı",hex:"#F3DA0B"},{code:"RAL 1023",name:"Trafik Sarısı",hex:"#FAD201"},{code:"RAL 1024",name:"Okr Sarısı",hex:"#AEA04B"},{code:"RAL 1026",name:"Parlak Sarı",hex:"#FFFF00"},{code:"RAL 1027",name:"Köri",hex:"#9D9101"},{code:"RAL 1028",name:"Kavun Sarısı",hex:"#F4A900"},{code:"RAL 1032",name:"Süpürge Sarısı",hex:"#D6AE01"},{code:"RAL 1033",name:"Yıldız Çiçeği Sarısı",hex:"#F3A505"},{code:"RAL 1034",name:"Pastel Sarısı",hex:"#EFA94A"},{code:"RAL 1035",name:"İnci Bej",hex:"#6A5D4D"},{code:"RAL 1036",name:"İnci Altın",hex:"#705335"},{code:"RAL 1037",name:"Güneş Sarısı",hex:"#F39F18"},
  // Turuncu
  {code:"RAL 2000",name:"Sarı Turuncu",hex:"#ED760E"},{code:"RAL 2001",name:"Kırmızı Turuncu",hex:"#C93C20"},{code:"RAL 2002",name:"Kan Kırmızısı",hex:"#CB2821"},{code:"RAL 2003",name:"Pastel Turuncu",hex:"#FF7514"},{code:"RAL 2004",name:"Saf Turuncu",hex:"#F44611"},{code:"RAL 2005",name:"Parlak Turuncu",hex:"#FF2301"},{code:"RAL 2007",name:"Parlak Açık Turuncu",hex:"#FFA420"},{code:"RAL 2008",name:"Koyu Portakal",hex:"#F75E25"},{code:"RAL 2009",name:"Trafik Turuncu",hex:"#F54021"},{code:"RAL 2010",name:"Sinyalizasyon Turuncu",hex:"#D84B20"},{code:"RAL 2011",name:"Koyu Turuncu",hex:"#EC7C26"},{code:"RAL 2012",name:"Somon Turuncu",hex:"#E55137"},{code:"RAL 2013",name:"İnci Turuncu",hex:"#C35831"},
  // Kırmızı
  {code:"RAL 3000",name:"Alev Kırmızısı",hex:"#AF2B1E"},{code:"RAL 3001",name:"Sinyalizasyon Kırmızısı",hex:"#A52019"},{code:"RAL 3002",name:"Karmen Kırmızısı",hex:"#A2231D"},{code:"RAL 3003",name:"Yakut Kırmızısı",hex:"#9B111E"},{code:"RAL 3004",name:"Mor Kırmızı",hex:"#75151E"},{code:"RAL 3005",name:"Şarap Kırmızısı",hex:"#5E2129"},{code:"RAL 3007",name:"Siyah Kırmızı",hex:"#412227"},{code:"RAL 3009",name:"Oksit Kırmızısı",hex:"#642424"},{code:"RAL 3011",name:"Kahverengi Kırmızı",hex:"#781F19"},{code:"RAL 3012",name:"Bej Kırmızı",hex:"#C1876B"},{code:"RAL 3013",name:"Domates Kırmızısı",hex:"#A12312"},{code:"RAL 3014",name:"Antika Pembe",hex:"#D36E70"},{code:"RAL 3015",name:"Açık Pembe",hex:"#EA899A"},{code:"RAL 3016",name:"Mercan Kırmızısı",hex:"#B32821"},{code:"RAL 3017",name:"Gül",hex:"#E63244"},{code:"RAL 3018",name:"Çilek Kırmızısı",hex:"#D53032"},{code:"RAL 3020",name:"Trafik Kırmızısı",hex:"#CC0605"},{code:"RAL 3022",name:"Somon Pembe",hex:"#D95030"},{code:"RAL 3024",name:"Parlak Kırmızı",hex:"#F80000"},{code:"RAL 3026",name:"Parlak Açık Kırmızı",hex:"#FE0000"},{code:"RAL 3027",name:"Frambuaz Kırmızısı",hex:"#C51D34"},{code:"RAL 3028",name:"Saf Kırmızı",hex:"#CB3234"},{code:"RAL 3031",name:"Doğu Kırmızısı",hex:"#B32428"},{code:"RAL 3032",name:"İnci Yakut Kırmızısı",hex:"#721422"},{code:"RAL 3033",name:"İnci Pembe",hex:"#B44C43"},
  // Mor/Lila
  {code:"RAL 4001",name:"Kırmızı Lila",hex:"#6D3F5B"},{code:"RAL 4002",name:"Kırmızı Menekşe",hex:"#922B3E"},{code:"RAL 4003",name:"Funda Moru",hex:"#DE4C8A"},{code:"RAL 4004",name:"Bordo Menekşe",hex:"#641C34"},{code:"RAL 4005",name:"Mavi Lila",hex:"#6C4675"},{code:"RAL 4006",name:"Trafik Moru",hex:"#A03472"},{code:"RAL 4007",name:"Mor Menekşe",hex:"#4A192C"},{code:"RAL 4008",name:"Sinyalizasyon Menekşesi",hex:"#924E7D"},{code:"RAL 4009",name:"Pastel Menekşe",hex:"#A18594"},{code:"RAL 4010",name:"Telemagenta",hex:"#CF3476"},{code:"RAL 4011",name:"İnci Menekşe",hex:"#8673A1"},{code:"RAL 4012",name:"İnci Böğürtlen",hex:"#6C6874"},
  // Mavi
  {code:"RAL 5000",name:"Menekşe Mavi",hex:"#354D73"},{code:"RAL 5001",name:"Yeşil Mavi",hex:"#1F3438"},{code:"RAL 5002",name:"Ultramarin Mavi",hex:"#20214F"},{code:"RAL 5003",name:"Safir Mavisi",hex:"#1D1E33"},{code:"RAL 5004",name:"Siyah Mavi",hex:"#18171C"},{code:"RAL 5005",name:"Sinyalizasyon Mavisi",hex:"#1E2460"},{code:"RAL 5007",name:"Parlak Mavi",hex:"#3E5F8A"},{code:"RAL 5008",name:"Gri Mavi",hex:"#26252D"},{code:"RAL 5009",name:"Azure Mavi",hex:"#025669"},{code:"RAL 5010",name:"Centiyane Mavisi",hex:"#0E294B"},{code:"RAL 5011",name:"Çelik Mavisi",hex:"#231A24"},{code:"RAL 5012",name:"Açık Mavi",hex:"#3B83BD"},{code:"RAL 5013",name:"Kobalt Mavisi",hex:"#1E213D"},{code:"RAL 5014",name:"Güvercin Mavisi",hex:"#606E8C"},{code:"RAL 5015",name:"Gök Mavisi",hex:"#2271B3"},{code:"RAL 5017",name:"Trafik Mavisi",hex:"#063971"},{code:"RAL 5018",name:"Turkuaz Mavi",hex:"#3F888F"},{code:"RAL 5019",name:"Capri Mavisi",hex:"#1B5583"},{code:"RAL 5020",name:"Okyanus Mavisi",hex:"#1D334A"},{code:"RAL 5021",name:"Su Mavisi",hex:"#256D7B"},{code:"RAL 5022",name:"Gece Mavisi",hex:"#252850"},{code:"RAL 5023",name:"Uzak Mavi",hex:"#49678D"},{code:"RAL 5024",name:"Pastel Mavi",hex:"#5D9B9B"},{code:"RAL 5025",name:"İnci Centiyane",hex:"#2A6478"},{code:"RAL 5026",name:"İnci Gece Mavisi",hex:"#102C54"},
  // Yeşil
  {code:"RAL 6000",name:"Patina Yeşili",hex:"#327662"},{code:"RAL 6001",name:"Zümrüt Yeşili",hex:"#28713E"},{code:"RAL 6002",name:"Yaprak Yeşili",hex:"#276235"},{code:"RAL 6003",name:"Zeytin Yeşili",hex:"#4B573E"},{code:"RAL 6004",name:"Mavi Yeşil",hex:"#0F4336"},{code:"RAL 6005",name:"Yosun Yeşili",hex:"#0C3A2D"},{code:"RAL 6006",name:"Gri Zeytin",hex:"#3D3C2F"},{code:"RAL 6007",name:"Şişe Yeşili",hex:"#283424"},{code:"RAL 6008",name:"Kahve Yeşili",hex:"#35382E"},{code:"RAL 6009",name:"Göknar Yeşili",hex:"#27352A"},{code:"RAL 6010",name:"Çimen Yeşili",hex:"#3E7242"},{code:"RAL 6011",name:"Rezeda Yeşili",hex:"#66804D"},{code:"RAL 6012",name:"Siyah Yeşil",hex:"#31403D"},{code:"RAL 6013",name:"Saz Yeşili",hex:"#797C5A"},{code:"RAL 6014",name:"Sarı Zeytin",hex:"#47402E"},{code:"RAL 6015",name:"Siyah Zeytin",hex:"#3B3C36"},{code:"RAL 6016",name:"Turkuaz Yeşil",hex:"#1E5945"},{code:"RAL 6017",name:"Mayıs Yeşili",hex:"#4E8F3D"},{code:"RAL 6018",name:"Sarı Yeşil",hex:"#57A639"},{code:"RAL 6019",name:"Pastel Yeşil",hex:"#BDECB6"},{code:"RAL 6020",name:"Krom Yeşili",hex:"#2E3A23"},{code:"RAL 6021",name:"Soluk Yeşil",hex:"#89AC76"},{code:"RAL 6022",name:"Zeytin Haki",hex:"#25221B"},{code:"RAL 6024",name:"Trafik Yeşili",hex:"#308446"},{code:"RAL 6025",name:"Eğreltiotu Yeşili",hex:"#3D642D"},{code:"RAL 6026",name:"Opal Yeşil",hex:"#015D52"},{code:"RAL 6027",name:"Açık Yeşil",hex:"#84C3BE"},{code:"RAL 6028",name:"Çam Yeşili",hex:"#2C5545"},{code:"RAL 6029",name:"Nane Yeşili",hex:"#20603D"},{code:"RAL 6032",name:"Sinyalizasyon Yeşili",hex:"#317F43"},{code:"RAL 6033",name:"Nane Turkuaz",hex:"#497E76"},{code:"RAL 6034",name:"Pastel Turkuaz",hex:"#7FB5B5"},{code:"RAL 6035",name:"İnci Yeşil",hex:"#1C542D"},{code:"RAL 6036",name:"İnci Opal Yeşil",hex:"#005F4E"},{code:"RAL 6037",name:"Saf Yeşil",hex:"#008F39"},{code:"RAL 6038",name:"Parlak Yeşil",hex:"#00F700"},
  // Gri
  {code:"RAL 7000",name:"Sincap Grisi",hex:"#78858B"},{code:"RAL 7001",name:"Gümüş Gri",hex:"#8A9597"},{code:"RAL 7002",name:"Zeytin Gri",hex:"#7E7B52"},{code:"RAL 7003",name:"Yosun Grisi",hex:"#6C7156"},{code:"RAL 7004",name:"Sinyalizasyon Grisi",hex:"#969992"},{code:"RAL 7005",name:"Fare Grisi",hex:"#646B63"},{code:"RAL 7006",name:"Bej Gri",hex:"#6D6552"},{code:"RAL 7008",name:"Haki Gri",hex:"#6A5F31"},{code:"RAL 7009",name:"Yeşil Gri",hex:"#4D5645"},{code:"RAL 7010",name:"Branda Grisi",hex:"#4C514A"},{code:"RAL 7011",name:"Demir Grisi",hex:"#434B4D"},{code:"RAL 7012",name:"Bazalt Grisi",hex:"#4E5754"},{code:"RAL 7013",name:"Kahve Gri",hex:"#464531"},{code:"RAL 7015",name:"Arduvaz Grisi",hex:"#434750"},{code:"RAL 7016",name:"Antrasit Gri",hex:"#293133"},{code:"RAL 7021",name:"Siyah Gri",hex:"#23282B"},{code:"RAL 7022",name:"Umbra Grisi",hex:"#332F2C"},{code:"RAL 7023",name:"Beton Grisi",hex:"#686C5E"},{code:"RAL 7024",name:"Grafit Grisi",hex:"#474A51"},{code:"RAL 7026",name:"Granit Grisi",hex:"#2F353B"},{code:"RAL 7030",name:"Taş Grisi",hex:"#8B8C7A"},{code:"RAL 7031",name:"Mavi Gri",hex:"#474B4E"},{code:"RAL 7032",name:"Çakıl Taşı Grisi",hex:"#B8B799"},{code:"RAL 7033",name:"Çimento Grisi",hex:"#7D8471"},{code:"RAL 7034",name:"Sarı Gri",hex:"#8F8B66"},{code:"RAL 7035",name:"Açık Gri",hex:"#D7D7D7"},{code:"RAL 7036",name:"Platin Gri",hex:"#7F7679"},{code:"RAL 7037",name:"Tozlu Gri",hex:"#7D7F7D"},{code:"RAL 7038",name:"Akik Grisi",hex:"#B5B8B1"},{code:"RAL 7039",name:"Kuvars Grisi",hex:"#6C6960"},{code:"RAL 7040",name:"Pencere Grisi",hex:"#9DA1AA"},{code:"RAL 7042",name:"Trafik Grisi A",hex:"#8D948D"},{code:"RAL 7043",name:"Trafik Grisi B",hex:"#4E5452"},{code:"RAL 7044",name:"İpek Gri",hex:"#CAC4B0"},{code:"RAL 7045",name:"Telegri 1",hex:"#909090"},{code:"RAL 7046",name:"Telegri 2",hex:"#82898F"},{code:"RAL 7047",name:"Telegri 4",hex:"#D0D0D0"},{code:"RAL 7048",name:"İnci Fare Grisi",hex:"#878176"},
  // Kahve
  {code:"RAL 8000",name:"Yeşil Kahve",hex:"#826C34"},{code:"RAL 8001",name:"Okr Kahve",hex:"#955F20"},{code:"RAL 8002",name:"Sinyalizasyon Kahvesi",hex:"#6C3B2A"},{code:"RAL 8003",name:"Kil Kahvesi",hex:"#734222"},{code:"RAL 8004",name:"Bakır Kahve",hex:"#8E402A"},{code:"RAL 8007",name:"Geyik Derisi Kahvesi",hex:"#59351F"},{code:"RAL 8008",name:"Zeytin Kahvesi",hex:"#6F4F28"},{code:"RAL 8011",name:"Ceviz Kahvesi",hex:"#28170B"},{code:"RAL 8012",name:"Kırmızı Kahve",hex:"#490A00"},{code:"RAL 8014",name:"Sepya Kahvesi",hex:"#382C1E"},{code:"RAL 8015",name:"Kestane Kahvesi",hex:"#633A34"},{code:"RAL 8016",name:"Maun Kahvesi",hex:"#4C2F27"},{code:"RAL 8017",name:"Çikolata Kahvesi",hex:"#45322E"},{code:"RAL 8019",name:"Gri Kahve",hex:"#403A3A"},{code:"RAL 8022",name:"Siyah Kahve",hex:"#212121"},{code:"RAL 8023",name:"Turuncu Kahve",hex:"#A65E2E"},{code:"RAL 8024",name:"Bej Kahve",hex:"#79553D"},{code:"RAL 8025",name:"Soluk Kahve",hex:"#755C48"},{code:"RAL 8028",name:"Terra Kahvesi",hex:"#4E3B31"},{code:"RAL 8029",name:"İnci Bakır",hex:"#763C28"},
  // Beyaz / Siyah
  {code:"RAL 9001",name:"Krem Beyaz",hex:"#FDF4E3"},{code:"RAL 9002",name:"Gri Beyaz",hex:"#E7EBDA"},{code:"RAL 9003",name:"Sinyalizasyon Beyazı",hex:"#F4F4F4"},{code:"RAL 9004",name:"Sinyalizasyon Siyahı",hex:"#282828"},{code:"RAL 9005",name:"Jet Siyahı",hex:"#0A0A0A"},{code:"RAL 9006",name:"Beyaz Alüminyum",hex:"#A5A5A5"},{code:"RAL 9007",name:"Gri Alüminyum",hex:"#8F8F8F"},{code:"RAL 9010",name:"Saf Beyaz",hex:"#FFFFFF"},{code:"RAL 9011",name:"Grafit Siyahı",hex:"#1C2023"},{code:"RAL 9016",name:"Trafik Beyazı",hex:"#F6F6F6"},{code:"RAL 9017",name:"Trafik Siyahı",hex:"#1E1E1E"},{code:"RAL 9018",name:"Papirüs Beyazı",hex:"#D7D7D7"},{code:"RAL 9022",name:"İnci Açık Gri",hex:"#9E9E9E"},{code:"RAL 9023",name:"İnci Koyu Gri",hex:"#82898F"},
];

// ─── RAL Renk Combobox (arama + minik renk kutusu) ─────────────────────────
export function RalCombobox({ value, onSelect, testid = "ral-combobox", extraRals = [] }) {
  const { tr, lang } = useContext(UICtx);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [customRals, setCustomRals] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zenCustomRals") || "[]"); }
    catch (_) { return []; }
  });
  const boxRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const mappedExtraRals = extraRals.map(r => ({ code: r.code || r.label, name: r.label, hex: r.hex || r.hexColor || "#ccc", asset: r }));
  const storedRals = customRals.map(r => ({ code: r.code, name: r.name || r.label, hex: r.hex || r.hexColor || "#ccc", asset: r }));
  const allRals = [...storedRals, ...mappedExtraRals, ...RAL_COLORS.filter(r => ![...storedRals, ...mappedExtraRals].some(er => er.code === r.code))];
  const norm = (s) => (s || "").toLowerCase();
  const filtered = q ? allRals.filter(r => norm(r.code).includes(norm(q)) || norm(r.name).includes(norm(q))) : allRals;
  const shown = filtered.slice(0, 80);
  const selected = allRals.find(r => r.code === value);
  const canAddCustom = q.trim() && !allRals.some(r => norm(r.code) === norm(q.trim()) || norm(r.name) === norm(q.trim()));
  const addCustomRal = async () => {
    const raw = q.trim();
    if (!raw) return;
    const code = window.prompt("Yeni renk kodu", raw.toUpperCase().startsWith("RAL") ? raw.toUpperCase() : raw) || raw;
    const name = window.prompt("Renk adı", raw) || raw;
    const hex = window.prompt("HEX renk kodu (opsiyonel)", "#CCCCCC") || "#CCCCCC";
    const currentStock = Number(window.prompt("Stok miktarı (opsiyonel)", "0") || 0);
    const stockNote = window.prompt("Stok notu (opsiyonel)", "") || "";
    const payload = { kind: "ral", label: name, code, hexColor: hex, currentStock, stockNote };
    try {
      const { data } = await api.post("/assets", payload);
      const row = { code: data.code || code, name: data.label || name, hex: data.hexColor || hex, currentStock: data.currentStock || currentStock, id: data.id };
      const next = [row, ...customRals.filter(r => r.code !== row.code)];
      setCustomRals(next);
      localStorage.setItem("zenCustomRals", JSON.stringify(next));
      onSelect(row.code);
      toast.success("Özel renk stoğa eklendi");
      setOpen(false); setQ("");
    } catch (e) {
      toast.error("Özel renk stoğa eklenemedi");
    }
  };
  return (
    <div className="relative flex-1 min-w-0" ref={boxRef} data-testid={testid}>
      <div
        className="zen-input !py-1.5 flex items-center gap-2 cursor-text"
        onClick={() => { setOpen(true); setQ(""); }}
      >
        {selected && <span className="w-4 h-4 rounded shrink-0 border border-slate-300 dark:border-[#22304A]" style={{ background: selected.hex }} />}
        <input
          className="bg-transparent outline-none flex-1 text-sm min-w-0"
          placeholder={tr("RAL kodu veya renk ara…")}
          value={open ? q : (selected ? `${selected.code} — ${selected.name}` : value || "")}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          data-testid={`${testid}-input`}
        />
        {value && <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(""); setOpen(false); }} className="text-slate-400 hover:text-rose-500 shrink-0"><X className="w-3.5 h-3.5" /></button>}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] shadow-xl" data-testid={`${testid}-list`}>
          {shown.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">{tr("Sonuç yok")}</div>}
          {shown.map(r => (
            <button key={r.code} type="button"
              className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-[#22304A] text-left transition-colors ${r.code === value ? "bg-slate-100 dark:bg-[#22304A]" : ""}`}
              onMouseDown={() => { onSelect(r.code); setOpen(false); setQ(""); }}
              data-testid={`${testid}-opt-${r.code.replace(/\s/g,"")}`}
            >
              <span className="w-5 h-5 rounded shrink-0 border border-slate-200 dark:border-[#333]" style={{ background: r.hex }} />
              <span className="font-mono font-bold text-[11px] text-slate-600 dark:text-slate-300 shrink-0 w-16">{r.code}</span>
              <span className="text-slate-700 dark:text-slate-200 truncate">{r.name}</span>
            </button>
          ))}
          {canAddCustom && (
            <button type="button" data-testid={`${testid}-add-custom`} onMouseDown={(e) => { e.preventDefault(); addCustomRal(); }} className="w-full px-3 py-2 text-left text-xs font-bold text-[#E5322A] hover:bg-[#E5322A]/5 border-t border-slate-100 dark:border-[#22304A]">
              + “{q.trim()}” için Özel Ekle
            </button>
          )}
          {filtered.length > 80 && <div className="px-3 py-1.5 text-[10px] text-slate-400">+{filtered.length - 80} sonuç daha — aramayı daraltın</div>}
        </div>
      )}
    </div>
  );
}

// ─── Kumaş maliyet / kâr marjı + stok dışı uyarısı ──────────────────────────
export function fabricMargin({ purchaseTL, m2, saleTotal, saleCurrency, rates }) {
  if (!purchaseTL || !m2 || !saleTotal) return null;
  const costTL = purchaseTL * m2;
  const saleTL = convertPrice(saleTotal, saleCurrency || "TL", "TL", rates, "revenue");
  const profit = saleTL - costTL;
  return { costTL, saleTL, profit, pct: saleTL > 0 ? (profit / saleTL) * 100 : 0 };
}

export function FabricStockPanel({ item, setItem, margin, saleUnit, saleCurrency, testid = "pf" }) {
  const { tr, lang } = useContext(UICtx);
  const stock = useContext(StockSettingsCtx);
  const oos = !!item.fabricOutOfStock;
  const purchase = Number(item.fabricPurchase || 0);
  const hasFabricStock = stock.enabled && item.fabricCurrentStock != null && item.fabricCode;
  const consumption = hasFabricStock ? (Number(item.widthCm || 0) / 100) * (Number(item.heightCm || 0) / 100) * (Number(item.qty) || 1) : 0;
  const insufficient = hasFabricStock && consumption > 0 && consumption > item.fabricCurrentStock;
  const lowStock = hasFabricStock && !insufficient && item.fabricCurrentStock < stock.threshold;
  if (!oos && !purchase && !insufficient && !lowStock) return null;
  return (
    <div className="space-y-2">
      {insufficient && (
        <div className="zen-panel !p-3 border-rose-500/40 bg-rose-500/10" data-testid={`${testid}-stock-insufficient`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
            <div className="text-[11px] leading-relaxed">
              <span className="font-black text-rose-500">{tr("YETERSİZ KUMAŞ STOĞU")}</span> — bu sipariş için {consumption.toFixed(1)}m gerekiyor,
              stokta sadece <b>{item.fabricCurrentStock.toFixed(1)}m</b> var. Onaylanınca stok eksiye düşecektir.
            </div>
          </div>
        </div>
      )}
      {lowStock && (
        <div className="zen-panel !p-3 border-amber-500/40 bg-amber-500/10" data-testid={`${testid}-stock-low`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="text-[11px] leading-relaxed">
              <span className="font-black text-amber-600">{tr("DÜŞÜK STOK")}</span> — bu kumaştan stokta sadece <b>{item.fabricCurrentStock.toFixed(1)}m</b> kaldı (eşik: {stock.threshold}m).
            </div>
          </div>
        </div>
      )}
      {oos && (
        <div className="zen-panel !p-3 border-rose-500/40 bg-rose-500/10" data-testid={`${testid}-stock-warning`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
            <div className="text-[11px] leading-relaxed">
              <span className="font-black text-rose-500">{tr("STOK DIŞI KUMAŞ")}</span> — bu kumaş tedarikçide stok dışıdır.
              {item.fabricStockNote ? <span className="text-slate-500"> ({item.fabricStockNote})</span> : null}
              <label className="flex items-center gap-2 mt-2 font-semibold cursor-pointer">
                <input type="checkbox" data-testid={`${testid}-stock-ack`} checked={!!item.stockAck}
                  onChange={(e) => setItem({ ...item, stockAck: e.target.checked })} />
                Stok dışı olduğunu biliyorum, yine de devam et
              </label>
            </div>
          </div>
        </div>
      )}
      {!oos && item.fabricStockNote && (
        <div className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold" data-testid={`${testid}-stock-note`}>
          ⚠ {item.fabricStockNote}
        </div>
      )}
      {purchase > 0 && item.groupSlug !== "tul_fon_perde" && (() => {
        // GÖRSEL DÜZELTME: alış fiyatı KENDİ para birimiyle gösterilir (TL değil!)
        const pcur = (item.fabricPurchaseCurrency || "TL").toUpperCase();
        const psym = pcur === "EUR" ? "€" : pcur === "USD" ? "$" : "₺";
        return (
        <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1" data-testid={`${testid}-margin`}>
          <span>{tr("Alış:")}<b className="text-slate-700 dark:text-slate-200">{psym}{purchase.toFixed(2)}/m²</b></span>
          {Number(saleUnit) > 0 && (() => {
            const scur = (saleCurrency || "TL").toUpperCase();
            const ssym = scur === "EUR" ? "€" : scur === "USD" ? "$" : "₺";
            return <span>{tr("Satış:")}<b className="text-slate-700 dark:text-slate-200">{ssym}{Number(saleUnit).toFixed(2)}/m²</b></span>;
          })()}
          {margin && (
            <>
              <span>{tr("Maliyet:")}<b className="text-slate-700 dark:text-slate-200">₺{margin.costTL.toFixed(2)}</b></span>
              <span className={margin.profit < 0 ? "text-rose-500 font-bold" : "text-emerald-600 dark:text-emerald-400 font-semibold"}>
                Kâr: ₺{margin.profit.toFixed(2)} ({margin.pct.toFixed(1)}%)
              </span>
              {margin.profit < 0 && <span className="text-rose-500 font-black" data-testid={`${testid}-loss-warning`}>{tr("ZARARINA SATIŞ!")}</span>}
            </>
          )}
        </div>
        );
      })()}
    </div>
  );
}

// Kumaş seçildiğinde item'a yazılacak stok/maliyet alanları
export const fabricStockFields = (f) => ({
  fabricPurchase: f?.purchasePrice || 0,
  fabricPurchaseCurrency: (f?.purchaseCurrency || f?.currency || "TL").toUpperCase(),
  fabricOutOfStock: !!f?.outOfStock,
  fabricStockNote: f?.stockNote || "",
  fabricCurrentStock: f ? (f.currentStock ?? 0) : null,
  stockAck: false,
});

// Tül/Fon kumaş kategorisi — group alanı öncelikli, sonra fabricCategory, sonra eni/isim
export function fabricCategoryOf(fabric) {
  if (!fabric) return "fon";
  const g = fabric.group || "";
  if (g === "tf_tul" || g === "tf_havuz") return "tül";
  if (g === "dosemelik" || g === "tf_dosemelik") return "döşemelik";
  if (g === "tf_fon") return "fon";
  if (Number(fabric.fabricWidthCm) === 140) return "döşemelik";
  if (fabric.fabricCategory) return fabric.fabricCategory;
  const name = (fabric.label || "").toLocaleLowerCase("tr");
  if (/tül|tul/.test(name)) return "tül";
  return "fon";
}

export function FabricCombobox({ options, value, display, onSelect, testid, formSlug }) {
  const { tr } = useContext(UICtx);
  const isTulFon = formSlug === "tul_fon_perde";
  const [open, setOpen]           = useState(false);
  const [q, setQ]                 = useState("");
  const [zoom, setZoom]           = useState(false);
  const [displayCount, setDisplayCount] = useState(80);
  const [outOfStockSeries, setOutOfStockSeries] = useState(new Set());
  const [dropPos, setDropPos]     = useState(null); // { top, left, width }
  const [quickAdd, setQuickAdd]   = useState({ show: false, name: "", purchase: "", kumasEni: "300", category: "fon" });
  const [customFabs, setCustomFabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`zenCF_${formSlug || "all"}`) || "[]"); }
    catch(_) { return []; }
  });
  const boxRef      = useRef(null);
  const inputRef    = useRef(null);
  const panelRef    = useRef(null);
  const sentinelRef = useRef(null);

  // OOS seri kodlarını çek (badge için)
  useEffect(() => {
    if (!formSlug) return;
    api.get(`/fabric-series?formSlug=${formSlug}&parentId=root`)
      .then(({ data }) => {
        setOutOfStockSeries(new Set(
          (data || []).filter(s => s.stockStatus === "out_of_stock").map(s => s.code)
        ));
      })
      .catch(() => {});
  }, [formSlug]);

  // Dışarı tıklayınca kapat
  useEffect(() => {
    const h = (e) => {
      if (
        boxRef.current && !boxRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) { setOpen(false); setZoom(false); }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Scroll/resize'da dropdown konumunu güncelle
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update, true); window.removeEventListener("resize", update); };
  }, [open]);

  // Arama değişince sayacı sıfırla
  useEffect(() => { setDisplayCount(80); }, [q]);

  // Lazy load sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setDisplayCount(c => c + 50); },
      { threshold: 0.1 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  });

  const norm = (s) => (s || "").toString().toLocaleLowerCase("tr");

  // Özel kumaşları birleştir
  const customAsOptions = useMemo(() => customFabs.map(f => ({
    value: f.value, label: f.label, fabric: f.fabric || { group: "custom", label: f.label }
  })), [customFabs]);
  const allOptions = useMemo(() => [...customAsOptions, ...options], [customAsOptions, options]);

  // Düz liste filtreleme — kod veya ada göre anlık
  const filtered = useMemo(() =>
    q.trim()
      ? allOptions.filter(o => norm(o.label).includes(norm(q)) || norm(o.value).includes(norm(q)))
      : allOptions
  , [allOptions, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown   = filtered.slice(0, displayCount);
  const hasMore = filtered.length > displayCount;
  const selected = allOptions.find(o => o.value === value);
  const selImg   = selected?.fabric?.imageBase64;

  const openCombo = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    setOpen(true); setQ("");
  };

  const addCustomFabric = async (opts) => {
    const { name, purchasePrice = 0, kumasEniCm = 300, category = "fon" } = opts;
    if (!name.trim()) return;
    const code = `TF-${Date.now()}`;
    const payload = {
      kind: "fabric", label: name.trim(), code,
      group: formSlug || "custom",
      groupSlugs: formSlug ? [formSlug] : [],
      price: 0, currency: "TL",
      purchasePrice: Number(purchasePrice) || 0, purchaseCurrency: "TL",
      currentStock: 0,
      ...(isTulFon ? { fabricWidthCm: Number(kumasEniCm) || 300, fabricCategory: category } : {}),
    };
    try {
      const { data } = await api.post("/assets", payload);
      const newFab = { value: data.code || code, label: data.label || name.trim(), fabric: data };
      const next = [newFab, ...customFabs.filter(f => f.value !== newFab.value)];
      setCustomFabs(next);
      try { localStorage.setItem(`zenCF_${formSlug || "all"}`, JSON.stringify(next)); } catch(_) {}
      onSelect({ value: newFab.value, label: newFab.label, fabric: data });
      toast.success("Kumaş stoğa eklendi");
      setOpen(false); setQ(""); setQuickAdd({ show: false, name: "", purchase: "", kumasEni: "300" });
    } catch {
      toast.error("Kumaş eklenemedi");
    }
  };

  // Dropdown panel — position:fixed ile overflow-hidden parent'tan kaçar
  const dropPanel = open && dropPos && ReactDOM.createPortal(
    <div
      ref={panelRef}
      style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
      className="max-h-72 overflow-auto rounded-md border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] shadow-2xl"
      data-testid={`${testid}-list`}
    >
      {/* Seçimi temizle */}
      {value && (
        <button type="button" data-testid={`${testid}-clear`}
          className="sticky top-0 block w-full text-left px-3 py-1.5 text-xs text-[#E5322A] hover:bg-slate-50 dark:hover:bg-[#22304A] bg-white dark:bg-[#131B2E] border-b border-slate-100 dark:border-[#22304A] z-10"
          onClick={() => { onSelect({ value: "", label: "", fabric: null }); setOpen(false); setQ(""); }}>
          {tr("✕ Seçimi temizle")}
        </button>
      )}
      {/* Eşleşme sayısı */}
      {q.trim() && (
        <div className="sticky top-0 px-3 py-1 text-[10px] text-slate-400 bg-white dark:bg-[#131B2E] border-b border-slate-100 dark:border-[#22304A] z-10">
          {filtered.length} sonuç
        </div>
      )}
      {shown.length === 0 && (
        <div className="px-3 py-2">
          {!isTulFon
            ? <span className="text-xs text-slate-500">{tr("Sonuç yok")}</span>
            : !quickAdd.show
              ? (
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">"{q.trim()}" bulunamadı.</p>
                  <button type="button"
                    className="text-xs text-[#E5322A] font-semibold hover:underline"
                    onClick={() => setQuickAdd({ show: true, name: q.trim(), purchase: "", kumasEni: "300" })}>
                    + Bu ürünü eklemek ister misiniz?
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5" onClick={e => e.stopPropagation()}>
                  <p className="text-[10px] font-bold text-slate-600 dark:text-slate-300 truncate">{quickAdd.name}</p>
                  <select
                    value={quickAdd.category}
                    className="zen-input !py-1 !text-xs w-full"
                    onChange={e => setQuickAdd(q2 => ({ ...q2, category: e.target.value }))}>
                    <option value="tül">Tül</option>
                    <option value="fon">Fon Perde</option>
                    <option value="döşemelik">Döşemelik Kumaş</option>
                  </select>
                  <input
                    type="number" placeholder="Alış Fiyatı (TL)" value={quickAdd.purchase}
                    className="zen-input !py-1 !text-xs w-full"
                    onChange={e => setQuickAdd(q2 => ({ ...q2, purchase: e.target.value }))} />
                  <input
                    type="number" placeholder="Kumaş Eni (cm)" value={quickAdd.kumasEni}
                    className="zen-input !py-1 !text-xs w-full"
                    onChange={e => setQuickAdd(q2 => ({ ...q2, kumasEni: e.target.value }))} />
                  <div className="flex gap-1.5">
                    <button type="button"
                      className="flex-1 py-1 rounded text-xs font-bold bg-[#E5322A] text-white hover:bg-[#c42820] transition-colors"
                      onClick={() => addCustomFabric({ name: quickAdd.name, purchasePrice: quickAdd.purchase, kumasEniCm: quickAdd.kumasEni, category: quickAdd.category })}>
                      Ekle
                    </button>
                    <button type="button"
                      className="flex-1 py-1 rounded text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 transition-colors"
                      onClick={() => setQuickAdd({ show: false, name: "", purchase: "", kumasEni: "300", category: "fon" })}>
                      İptal
                    </button>
                  </div>
                </div>
              )
          }
        </div>
      )}
      {(() => {
        const renderOpt = (o) => {
          const isSeriesOOS = outOfStockSeries.has(o.fabric?.group);
          const isOOS = o.fabric?.outOfStock || isSeriesOOS;
          const nameOnly = o.label !== o.value ? o.label.replace(o.value, "").trim() : "";
          return (
            <button
              key={o.value}
              type="button"
              data-testid={`${testid}-opt-${o.value}`}
              className={`flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors
                ${o.value === value ? "bg-slate-100 dark:bg-[#22304A] font-bold" : ""}
                ${isSeriesOOS ? "opacity-55" : ""}`}
              onClick={() => { onSelect(o); setOpen(false); setQ(""); }}
            >
              <span className="w-7 h-7 shrink-0 rounded border border-slate-200 dark:border-[#22304A] overflow-hidden bg-slate-100 dark:bg-[#0C1220]">
                {o.fabric?.imageBase64
                  ? <img src={o.fabric.imageBase64} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : <span className="block w-full h-full" style={{ background: o.fabric?.hex || "transparent" }} />}
              </span>
              <span className={`flex-1 min-w-0 truncate ${isSeriesOOS ? "line-through text-slate-400 dark:text-slate-500" : ""}`}>
                {o.value?.startsWith("TF-") ? (
                  <span className="font-semibold">{o.label}</span>
                ) : (
                  <>
                    <span className="font-semibold">{o.value}</span>
                    {nameOnly && <span className="text-slate-500 dark:text-slate-400 ml-1 font-normal">{nameOnly}</span>}
                  </>
                )}
              </span>
              {isOOS && (
                <span className="ml-auto shrink-0 px-1.5 py-0.5 rounded text-[9px] font-black bg-rose-500/15 text-rose-500 border border-rose-500/30 whitespace-nowrap">
                  {isSeriesOOS ? tr("SERİ STOK DIŞI") : tr("STOK DIŞI")}
                </span>
              )}
            </button>
          );
        };

        // Tül/Fon formunda arama yoksa gruplu göster
        if (isTulFon && !q.trim()) {
          const CAT_LABELS = { "tül": "TÜLLER", "fon": "FON PERDELER", "döşemelik": "DÖŞEMELİK KUMAŞLAR" };
          return ["tül", "fon", "döşemelik"].map(cat => {
            const catItems = shown.filter(o => fabricCategoryOf(o.fabric) === cat);
            if (catItems.length === 0) return null;
            return (
              <div key={cat}>
                <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-[#0C1220] border-b border-t border-slate-100 dark:border-[#22304A]">
                  {CAT_LABELS[cat]}
                </div>
                {catItems.map(renderOpt)}
              </div>
            );
          });
        }

        // Arama varsa veya tül/fon değilse düz liste
        return shown.map(renderOpt);
      })()}
      {hasMore && (
        <div ref={sentinelRef} className="py-2 text-center text-[10px] text-slate-400">
          {shown.length} / {filtered.length} gösteriliyor…
        </div>
      )}
      {q.trim() && !allOptions.some(o => norm(o.label) === norm(q.trim()) || norm(o.value) === norm(q.trim())) && (
        <button type="button" data-testid={`${testid}-add-custom`}
          onClick={() => addCustomFabric({ name: q })}
          className="w-full px-3 py-2 text-left text-xs font-bold text-[#E5322A] hover:bg-[#E5322A]/5 border-t border-slate-100 dark:border-[#22304A]">
          + "{q.trim()}" için Özel Ekle
        </button>
      )}
    </div>,
    document.body
  );

  return (
    <div className="relative flex-1 min-w-0 flex items-center gap-2" ref={boxRef}>
      {/* Seçili kumaş küçük swatch önizleme */}
      {value && (
        <button
          type="button" onClick={() => setZoom(z => !z)} data-testid={`${testid}-swatch`}
          title={selected?.label || ""}
          className="w-9 h-9 shrink-0 rounded-md border border-slate-300 dark:border-[#22304A] overflow-hidden bg-slate-100 dark:bg-[#0C1220] hover:ring-2 hover:ring-[#E5322A]/50 transition-all"
        >
          {selImg
            ? <img src={selImg} alt="" className="w-full h-full object-cover" />
            : <span className="block w-full h-full" style={{ background: selected?.fabric?.hex || "transparent" }} />}
        </button>
      )}
      {zoom && selImg && (
        <div className="absolute left-0 top-11 z-[60] p-2 rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#131B2E] shadow-2xl" data-testid={`${testid}-zoom`}>
          <img src={selImg} alt="" className="w-40 h-40 object-cover rounded-md" />
          <div className="mt-1 text-[11px] font-bold text-center max-w-40 truncate">{selected?.label}</div>
        </div>
      )}
      <div className="relative flex-1 min-w-0">
        <input
          ref={inputRef}
          data-testid={testid}
          className="zen-input !py-1 w-full"
          placeholder={tr("Kumaş ara (kod veya isim)…")}
          value={open ? q : (display || "")}
          onFocus={openCombo}
          onChange={(e) => { setQ(e.target.value); if (!open) openCombo(); }}
        />
        {dropPanel}
      </div>
    </div>
  );
}

export function SelectRow({ label, field, options, extras, testid, item, onChange }) {
  const { tr } = useContext(UICtx);
  const selected = options.find(o => (o.value ?? o.label) === item[field]);
  return (
    <div className="flex items-center gap-2.5">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-36 shrink-0">{label}</label>
      <div className="flex-1 relative">
        <select
          data-testid={testid}
          className="zen-input !py-1 !text-xs w-full"
          value={item[field] || ""}
          onChange={(e) => onChange(field, e.target.value)}
        >
          <option value="">{tr("Seçiniz")}</option>
          {options.map((opt, i) => (
            <option key={opt.value ?? opt.id ?? i} value={opt.value ?? opt.label}>{opt.label}{opt.code ? ` (${opt.code})` : ""}</option>
          ))}
          {extras}
        </select>
        {selected && (selected.label.includes("·") || selected.label.includes("+")) && (
          <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#E5322A] pointer-events-none">{selected.label.split("·").slice(1).join("·").trim() || selected.label.split("+").slice(1).join("+").trim() ? "↑" : ""}</span>
        )}
      </div>
    </div>
  );
}
